import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { Sparkles, MessageSquare, PenLine } from 'lucide-react';

const copy = {
  en: {
    heading: 'When the words feel hard, JacKey helps you begin.',
    subheading: 'JacKey asks thoughtful questions based on what you\'re trying to say — apology, gratitude, love, closure, or something harder.',
    steps: [
      { icon: 'select', label: 'Select message type', desc: '"I want to say thank you"' },
      { icon: 'ask', label: 'JacKey asks questions', desc: '"What moment comes to mind first?"' },
      { icon: 'write', label: 'Your draft begins', desc: 'Guided words flow naturally' },
    ],
  },
  es: {
    heading: 'Cuando las palabras son difíciles, JacKey te ayuda a empezar.',
    subheading: 'JacKey hace preguntas reflexivas basadas en lo que intentas decir — disculpa, gratitud, amor, cierre o algo más difícil.',
    steps: [
      { icon: 'select', label: 'Selecciona tipo de mensaje', desc: '"Quiero dar las gracias"' },
      { icon: 'ask', label: 'JacKey hace preguntas', desc: '"¿Qué momento te viene a la mente primero?"' },
      { icon: 'write', label: 'Tu borrador comienza', desc: 'Las palabras guiadas fluyen naturalmente' },
    ],
  },
  pt: { heading: 'Quando as palavras são difíceis, JacKey ajuda você a começar.', subheading: 'JacKey faz perguntas reflexivas baseadas no que você está tentando dizer — desculpa, gratidão, amor, encerramento ou algo mais difícil.', steps: [{ icon: 'select', label: 'Selecione o tipo de mensagem', desc: '"Quero agradecer"' }, { icon: 'ask', label: 'JacKey faz perguntas', desc: '"Que momento vem à mente primeiro?"' }, { icon: 'write', label: 'Seu rascunho começa', desc: 'Palavras guiadas fluem naturalmente' }] },
  fr: { heading: 'Quand les mots sont difficiles, JacKey vous aide à commencer.', subheading: 'JacKey pose des questions réfléchies basées sur ce que vous essayez de dire — excuses, gratitude, amour, clôture ou quelque chose de plus difficile.', steps: [{ icon: 'select', label: 'Sélectionnez le type de message', desc: '"Je veux dire merci"' }, { icon: 'ask', label: 'JacKey pose des questions', desc: '"Quel moment vous vient à l\'esprit en premier ?"' }, { icon: 'write', label: 'Votre brouillon commence', desc: 'Les mots guidés coulent naturellement' }] },
  ar: { heading: 'عندما تكون الكلمات صعبة، JacKey يساعدك على البدء.', subheading: 'JacKey يطرح أسئلة مدروسة بناءً على ما تحاول قوله.', steps: [{ icon: 'select', label: 'اختر نوع الرسالة', desc: '"أريد أن أقول شكرًا"' }, { icon: 'ask', label: 'JacKey يطرح أسئلة', desc: '"ما اللحظة التي تتبادر إلى ذهنك أولاً?"' }, { icon: 'write', label: 'مسودتك تبدأ', desc: 'الكلمات الموجهة تتدفق بشكل طبيعي' }] },
  ja: { heading: '言葉が難しいとき、JacKeyが始めるお手伝いをします。', subheading: 'JacKeyは、あなたが伝えようとしていることに基づいて思慮深い質問をします。', steps: [{ icon: 'select', label: 'メッセージタイプを選択', desc: '「感謝を伝えたい」' }, { icon: 'ask', label: 'JacKeyが質問', desc: '「最初に思い浮かぶ瞬間は？」' }, { icon: 'write', label: '下書きが始まる', desc: 'ガイドされた言葉が自然に流れます' }] },
  ko: { heading: '말이 어려울 때, JacKey가 시작을 도와줍니다.', subheading: 'JacKey는 당신이 전하려는 것에 기반한 사려 깊은 질문을 합니다.', steps: [{ icon: 'select', label: '메시지 유형 선택', desc: '"감사를 전하고 싶어요"' }, { icon: 'ask', label: 'JacKey가 질문합니다', desc: '"가장 먼저 떠오르는 순간은?"' }, { icon: 'write', label: '초안이 시작됩니다', desc: '안내된 말이 자연스럽게 흐릅니다' }] },
  id: { heading: 'Saat kata-kata terasa sulit, JacKey membantu Anda memulai.', subheading: 'JacKey mengajukan pertanyaan bijaksana berdasarkan apa yang ingin Anda sampaikan.', steps: [{ icon: 'select', label: 'Pilih jenis pesan', desc: '"Saya ingin mengucapkan terima kasih"' }, { icon: 'ask', label: 'JacKey bertanya', desc: '"Momen apa yang pertama kali terlintas?"' }, { icon: 'write', label: 'Draf Anda dimulai', desc: 'Kata-kata yang dipandu mengalir secara alami' }] },

  hi: {
    heading: 'जब शब्द मुश्किल लगते हैं, तो JacKey आपको शुरू करने में मदद करता है।',
    subheading: 'JacKey इस बात पर आधारित विचारशील प्रश्न पूछता है कि आप क्या कहने की कोशिश कर रहे हैं — माफी, कृतज्ञता, प्यार, समापन, या कुछ और मुश्किल।',
    steps: [
      { icon: 'select', label: 'Select message type', desc: '"I want to say thank you"' },
      { icon: 'ask', label: 'JacKey asks questions', desc: '"What moment comes to mind first?"' },
      { icon: 'लिखें', label: 'आपका ड्राफ्ट शुरू होता है', desc: 'निर्देशित शब्द स्वाभाविक रूप से प्रवाहित होते हैं' },
    ],
  },
};

const iconMap = {
  select: MessageSquare,
  ask: Sparkles,
  write: PenLine,
};

export function JacKeySection() {
  const { language } = useLanguage();
  const t = lc(copy, language);

  return (
    <section className="py-20 md:py-28 bg-muted/20">
      <div className="container max-w-4xl mx-auto text-center">
        <motion.h2
          className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold billboard-flash mb-5 !leading-tight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {t.heading}
        </motion.h2>
        <motion.p
          className="text-lg text-muted-foreground mb-14 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          {t.subheading}
        </motion.p>

        <div className="grid md:grid-cols-3 gap-6">
          {t.steps.map((step, i) => {
            const Icon = iconMap[step.icon as keyof typeof iconMap];
            return (
              <motion.div
                key={i}
                className="flex flex-col items-center gap-4 p-7 rounded-2xl bg-card border border-border/30 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
              >
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-base font-semibold text-foreground">{step.label}</h3>
                <p className="text-sm text-muted-foreground italic">{step.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
